import {
  BoxGeometry,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PointLight,
  type Scene,
} from 'three';
import { BlockType, type Vector3D } from '../types';
import type { WorldManager } from './WorldManager';

interface ExitPortal {
  group: Group;
  light: PointLight;
  sheetMat: MeshBasicMaterial;
  coreMat: MeshBasicMaterial;
  haloMat: MeshBasicMaterial;
  markMat: MeshBasicMaterial;
  phase: number;
  position: Vector3D;
}

/**
 * Freestanding glowing gate for each ExitDoor.
 * ExitDoor voxels are invisible logic triggers; this mesh is the visible exit.
 */
export class ExitPortalDecor {
  private readonly portals: ExitPortal[] = [];
  private readonly scene: Scene;
  private readonly world: WorldManager;
  private readonly blockSize: number;
  private locked = false;
  private lightsEnabled = true;

  private readonly boxGeo = new BoxGeometry(1, 1, 1);
  private readonly stoneMat = new MeshStandardMaterial({
    color: 0x2e2824,
    roughness: 0.9,
    metalness: 0.05,
    emissive: new Color(0x1a1008),
    emissiveIntensity: 0.2,
  });
  private readonly trimMat = new MeshStandardMaterial({
    color: 0xe8a848,
    roughness: 0.4,
    metalness: 0.6,
    emissive: new Color(0xff7a20),
    emissiveIntensity: 0.55,
  });

  constructor(scene: Scene, world: WorldManager, blockSize: number) {
    this.scene = scene;
    this.world = world;
    this.blockSize = blockSize;
  }

  /** Soft-lock visual: red/dim while a boss is alive. */
  setLocked(locked: boolean): void {
    this.locked = locked;
    this.applyLockColors();
  }

  /** PointLights are expensive on weak GPUs — disable on low quality. */
  setLightsEnabled(enabled: boolean): void {
    this.lightsEnabled = enabled;
    for (const p of this.portals) {
      p.light.visible = enabled;
      if (!enabled) p.light.intensity = 0;
    }
  }

  /**
   * Rebuild props for ExitDoors in an optional inclusive Y band.
   * Omitting the range keeps previous whole-world behavior.
   */
  rebuild(minY?: number, maxY?: number): void {
    this.clear();
    const w = this.world.width;
    const h = this.world.height;
    const d = this.world.depth;
    const y0 = minY === undefined ? 0 : Math.max(0, minY);
    const y1 = maxY === undefined ? h - 1 : Math.min(h - 1, maxY);

    for (let y = y0; y <= y1; y++) {
      for (let z = 0; z < d; z++) {
        for (let x = 0; x < w; x++) {
          if (this.world.getBlock(x, y, z) !== BlockType.ExitDoor) continue;
          // Only decorate the bottom door voxel of a stack.
          if (this.world.getBlock(x, y - 1, z) === BlockType.ExitDoor) continue;
          this.spawnAt(x, y, z);
        }
      }
    }
  }

  clear(): void {
    for (let i = this.portals.length - 1; i >= 0; i--) {
      this.destroyPortal(this.portals[i]!);
    }
  }

  update(dt: number): void {
    for (const p of this.portals) {
      p.phase += dt * (this.locked ? 1.2 : 2.6);
      const pulse = 0.5 + Math.sin(p.phase) * 0.4;
      if (this.locked) {
        if (this.lightsEnabled) p.light.intensity = 0.6 + pulse * 0.5;
        p.sheetMat.opacity = 0.25 + pulse * 0.15;
        p.coreMat.opacity = 0.3 + pulse * 0.15;
        p.haloMat.opacity = 0.08 + pulse * 0.08;
        p.markMat.opacity = 0.4 + pulse * 0.1;
      } else {
        if (this.lightsEnabled) p.light.intensity = 2.0 + pulse * 1.8;
        p.sheetMat.opacity = 0.45 + pulse * 0.35;
        p.coreMat.opacity = 0.55 + pulse * 0.3;
        p.haloMat.opacity = 0.15 + pulse * 0.18;
        p.markMat.opacity = 0.75 + pulse * 0.2;
      }
    }
  }

  private applyLockColors(): void {
    const sheet = this.locked ? 0xaa2020 : 0xff7a28;
    const core = this.locked ? 0xff8080 : 0xffe8b0;
    const halo = this.locked ? 0x881818 : 0xff9a40;
    const mark = this.locked ? 0xffc0c0 : 0xfff6d8;
    const light = this.locked ? 0xaa3030 : 0xff8a30;
    for (const p of this.portals) {
      p.sheetMat.color.setHex(sheet);
      p.coreMat.color.setHex(core);
      p.haloMat.color.setHex(halo);
      p.markMat.color.setHex(mark);
      p.light.color.setHex(light);
    }
  }

  dispose(): void {
    this.clear();
    this.boxGeo.dispose();
    this.stoneMat.dispose();
    this.trimMat.dispose();
  }

  private spawnAt(vx: number, vy: number, vz: number): void {
    const facing = this.facingIntoRoom(vx, vy, vz);
    const bs = this.blockSize;

    // Push the whole gate into the room so it isn't buried in the wall.
    const standOff = 0.55;
    const cx = (vx + 0.5) * bs + facing.x * standOff;
    const cy = vy * bs;
    const cz = (vz + 0.5) * bs + facing.z * standOff;

    const group = new Group();
    group.position.set(cx, cy, cz);
    // Local +Z faces the player (into the room).
    group.rotation.y = Math.atan2(facing.x, facing.z);

    // —— Frame ——
    const pillarL = new Mesh(this.boxGeo, this.stoneMat);
    pillarL.scale.set(0.32, 2.35, 0.38);
    pillarL.position.set(-0.72, 1.15, 0);

    const pillarR = new Mesh(this.boxGeo, this.stoneMat);
    pillarR.scale.set(0.32, 2.35, 0.38);
    pillarR.position.set(0.72, 1.15, 0);

    const lintel = new Mesh(this.boxGeo, this.stoneMat);
    lintel.scale.set(1.76, 0.32, 0.4);
    lintel.position.set(0, 2.4, 0);

    const baseL = new Mesh(this.boxGeo, this.stoneMat);
    baseL.scale.set(0.4, 0.18, 0.48);
    baseL.position.set(-0.72, 0.09, 0.02);
    const baseR = new Mesh(this.boxGeo, this.stoneMat);
    baseR.scale.set(0.4, 0.18, 0.48);
    baseR.position.set(0.72, 0.09, 0.02);

    // —— Gold trim ——
    const trimL = new Mesh(this.boxGeo, this.trimMat);
    trimL.scale.set(0.07, 2.1, 0.07);
    trimL.position.set(-0.52, 1.15, 0.18);
    const trimR = new Mesh(this.boxGeo, this.trimMat);
    trimR.scale.set(0.07, 2.1, 0.07);
    trimR.position.set(0.52, 1.15, 0.18);
    const trimTop = new Mesh(this.boxGeo, this.trimMat);
    trimTop.scale.set(1.2, 0.07, 0.07);
    trimTop.position.set(0, 2.22, 0.18);

    for (const [sx, sy] of [
      [-0.52, 2.22],
      [0.52, 2.22],
      [-0.52, 0.28],
      [0.52, 0.28],
    ] as const) {
      const stud = new Mesh(this.boxGeo, this.trimMat);
      stud.scale.set(0.12, 0.12, 0.12);
      stud.position.set(sx, sy, 0.22);
      group.add(stud);
    }

    // —— Portal energy ——
    const sheetMat = new MeshBasicMaterial({
      color: 0xff7a28,
      transparent: true,
      opacity: 0.65,
      depthWrite: false,
      side: 2,
    });
    const sheet = new Mesh(this.boxGeo, sheetMat);
    sheet.scale.set(0.95, 2.0, 0.1);
    sheet.position.set(0, 1.15, 0.05);

    const coreMat = new MeshBasicMaterial({
      color: 0xffe8b0,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      side: 2,
    });
    const core = new Mesh(this.boxGeo, coreMat);
    core.scale.set(0.55, 1.55, 0.08);
    core.position.set(0, 1.15, 0.1);

    const haloMat = new MeshBasicMaterial({
      color: 0xff9a40,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
      side: 2,
    });
    const halo = new Mesh(this.boxGeo, haloMat);
    halo.scale.set(1.15, 2.2, 0.06);
    halo.position.set(0, 1.15, 0);

    const markMat = new MeshBasicMaterial({
      color: 0xfff6d8,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    });
    const chev1 = new Mesh(this.boxGeo, markMat);
    chev1.scale.set(0.42, 0.1, 0.04);
    chev1.position.set(0, 1.45, 0.16);
    const chev2 = new Mesh(this.boxGeo, markMat);
    chev2.scale.set(0.3, 0.09, 0.04);
    chev2.position.set(0, 1.25, 0.16);
    const chev3 = new Mesh(this.boxGeo, markMat);
    chev3.scale.set(0.18, 0.08, 0.04);
    chev3.position.set(0, 1.05, 0.16);

    const threshold = new Mesh(this.boxGeo, this.trimMat);
    threshold.scale.set(1.35, 0.1, 0.55);
    threshold.position.set(0, 0.05, 0.2);

    const light = new PointLight(0xff8a30, 2.8, 12, 1.5);
    light.position.set(0, 1.3, 0.55);
    light.visible = this.lightsEnabled;
    if (!this.lightsEnabled) light.intensity = 0;

    group.add(
      pillarL,
      pillarR,
      lintel,
      baseL,
      baseR,
      trimL,
      trimR,
      trimTop,
      sheet,
      core,
      halo,
      chev1,
      chev2,
      chev3,
      threshold,
      light,
    );
    this.scene.add(group);

    this.portals.push({
      group,
      light,
      sheetMat,
      coreMat,
      haloMat,
      markMat,
      phase: Math.random() * Math.PI * 2,
      position: { x: cx, y: cy, z: cz },
    });
    this.applyLockColors();
  }

  /** Unit vector from door toward open air (into the room). */
  private facingIntoRoom(vx: number, vy: number, vz: number): { x: number; z: number } {
    const candidates: Array<{ x: number; z: number; score: number }> = [
      { x: 1, z: 0, score: 0 },
      { x: -1, z: 0, score: 0 },
      { x: 0, z: 1, score: 0 },
      { x: 0, z: -1, score: 0 },
    ];
    for (const c of candidates) {
      let open = 0;
      for (let i = 1; i <= 4; i++) {
        const bx = vx + c.x * i;
        const bz = vz + c.z * i;
        const b0 = this.world.getBlock(bx, vy, bz);
        const b1 = this.world.getBlock(bx, vy + 1, bz);
        if (b0 === BlockType.Air && (b1 === BlockType.Air || b1 === BlockType.ExitDoor)) {
          open += 2;
        } else if (!this.world.isSolid(bx, vy, bz)) {
          open += 1;
        }
      }
      c.score = open;
    }
    candidates.sort((a, b) => b.score - a.score);
    const best = candidates[0]!;
    if (best.score === 0) {
      const cx = this.world.width / 2;
      const cz = this.world.depth / 2;
      const dx = cx - vx;
      const dz = cz - vz;
      const len = Math.hypot(dx, dz) || 1;
      return { x: dx / len, z: dz / len };
    }
    return { x: best.x, z: best.z };
  }

  private destroyPortal(p: ExitPortal): void {
    const idx = this.portals.indexOf(p);
    if (idx >= 0) this.portals.splice(idx, 1);
    p.sheetMat.dispose();
    p.coreMat.dispose();
    p.haloMat.dispose();
    p.markMat.dispose();
    p.group.removeFromParent();
  }
}
